import React, { useEffect } from "react";
import { CheckCircle as CheckCircleIcon, Star as StarIcon } from "@mui/icons-material";
import Logger from "../../services/Logger";
import { Box, Button, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { SURFACE } from "../../dark-theme/palette";
import { useConfetti } from "../../context/ConfettiContext";
import UserService from "../../services/UserService";
import { usePagesTranslation } from "../locales";
import StripeClimateBadgeIframe from "../components/StripeClimateBadgeIframe";
import { getVisibleSubscriptionFeatureKeys, EMPHASIZED_FEATURE_KEY } from "./PanelPlan";
import { trackEvent } from "../../services/GoogleAnalytics";

const SubscriptionSuccess = () => {
    const { t } = usePagesTranslation();
    const navigate = useNavigate();
    const { triggerConfetti } = useConfetti();

    useEffect(() => {
        trackEvent("entered", { name: "SubscriptionSuccess" });
        // Refresh user info to update pay plan after subscription
        const refreshUserInfo = async () => {
            try {
                await UserService.getUserInfo();
            } catch (error) {
                Logger.error("Error refreshing user info:", error);
            }
        };

        refreshUserInfo();

        // Trigger confetti when the page loads
        triggerConfetti(10 * 1000); // 10 seconds of confetti
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Only run once on mount

    const handleStartPlaying = () => {
        navigate('/');
    };

    return (
        <Box sx={{
            height: 'calc(100vh - 64px)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            p: 3
        }}>
            <Box sx={{
                maxWidth: 600,
                width: '100%',
                textAlign: 'center',
                backgroundColor: SURFACE.card,
                borderRadius: 3,
                p: 4,
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
            }}>
                <CheckCircleIcon sx={{
                    fontSize: 80,
                    color: '#4caf50',
                    mb: 2
                }} />
                
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 2 }}>
                    <StarIcon sx={{ fontSize: 32, color: '#ffd700' }} />
                    <Typography variant="h3" component="h1" sx={{ fontWeight: 700, color: '#3BA666' }}>
                        {t("subs.success.title")}
                    </Typography>
                    <StarIcon sx={{ fontSize: 32, color: '#ffd700' }} />
                </Box>

                <Typography variant="h5" color="text.secondary" sx={{ mb: 3 }}>
                    {t("subs.success.thank-you")}
                </Typography>

                <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.8 }}>
                    {t("subs.success.activated")}
                </Typography>

                <Box sx={{
                    backgroundColor: "rgba(76, 175, 80, 0.16)",
                    borderRadius: 2,
                    p: 3,
                    mb: 4,
                    textAlign: 'left'
                }}>
                    {getVisibleSubscriptionFeatureKeys().map((featureKey, index, arr) => {
                        const emphasized = featureKey === EMPHASIZED_FEATURE_KEY;
                        return (
                            <Box
                                key={featureKey}
                                sx={{ mb: index < arr.length - 1 ? 2 : 0 }}
                            >
                                <Typography
                                    variant="body2"
                                    component="div"
                                    sx={{
                                        fontWeight: 600,
                                        color: emphasized ? "#3BA666" : "inherit"
                                    }}
                                >
                                    ✓ {t(`subs.feature.${featureKey}.title`)}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, pl: 0, lineHeight: 1.6 }}>
                                    {t(`subs.feature.${featureKey}.desc`)}
                                </Typography>
                            </Box>
                        );
                    })}
                </Box>

                <Button
                    variant="contained"
                    size="large"
                    onClick={handleStartPlaying}
                    sx={{
                        backgroundColor: '#3BA666',
                        '&:hover': { backgroundColor: '#2e7d32' },
                        px: 4,
                        py: 1.5,
                        fontSize: '1.1rem',
                        fontWeight: 600
                    }}
                >
                    {t("subs.success.start-playing")}
                </Button>

                <StripeClimateBadgeIframe sx={{ mt: 3 }} />
            </Box>
        </Box>
    );
};

export default SubscriptionSuccess;

